import * as CL from '@design-system/component-library';

import { dsClass } from '../../common/constants/dsClasses';
import {
  alvZeroMsg,
  contractMsg,
  elisaDevicesServiceMsg,
  eurosPerMonthCapMsg,
  monthMsg,
  oneTimePaymentMsg,
  purchaseOptionsMsg,
  purchasedDeviceMsg,
  t,
  totalWithCurlyBracketsMsg,
} from '../../common/i18n/index.js';
import { eurosToCents, formatSum } from '../../common/utils/priceUtils.js';
import type { InstallmentOption } from '../../generated/api/installmentOption.js';
import type { InstallmentTerm } from '../../generated/api/installmentTerm.js';

import './DeviceDetailsPaymentCoCo.scss';

interface DeviceDetailsPaymentCoCoProps {
  deviceInstallmentOptions: InstallmentOption[];
  selectedDeviceInstallmentId: string;
  onSelectDeviceInstallment: (installmentId: string) => void;
  eppInstallmentTerms?: InstallmentTerm[];
  selectedEppInstallmentTermId?: string;
  onSelectEppInstallmentTerm?: (termId: string) => void;
}

const sortByPriceAndPeriod = <T extends { priceWithoutTax?: number }>(
  items: T[],
  getPeriod: (item: T) => number
): T[] =>
  [...items].sort((a, b) => {
    const aPrice = a.priceWithoutTax ?? Number.POSITIVE_INFINITY;
    const bPrice = b.priceWithoutTax ?? Number.POSITIVE_INFINITY;

    if (aPrice !== bPrice) {
      return aPrice - bPrice;
    }

    return getPeriod(a) - getPeriod(b);
  });

export const sortInstallmentOptionsForDisplay = (installmentOptions: InstallmentOption[]): InstallmentOption[] => {
  const regular = sortByPriceAndPeriod(
    installmentOptions.filter(o => o.numberOfInstallment !== 1),
    o => o.numberOfInstallment
  );
  const oneTime = installmentOptions.filter(o => o.numberOfInstallment === 1);
  return [...regular, ...oneTime];
};

export const sortInstallmentTermsForDisplay = (installmentTerms: InstallmentTerm[]): InstallmentTerm[] =>
  sortByPriceAndPeriod(installmentTerms, term => term.duration);

const formatInstallmentPrice = (priceWithoutTax: number | undefined, isOneTime: boolean) => {
  const formattedPrice =
    typeof priceWithoutTax === 'number' ? formatSum(eurosToCents(priceWithoutTax), true) : undefined;
  const priceUnit = isOneTime ? '€' : t.H37G(eurosPerMonthCapMsg);
  return formattedPrice ? `${formattedPrice} ${priceUnit}` : '-';
};

const installmentOptionToSelectItem = (option: InstallmentOption) => ({
  id: String(option.numberOfInstallment),
  name:
    option.numberOfInstallment === 1 ? t.ASEI(oneTimePaymentMsg) : `${option.numberOfInstallment} ${t.XXVX(monthMsg)}`,
  description: formatInstallmentPrice(option.priceWithoutTax, option.numberOfInstallment === 1),
});

const installmentTermToSelectItem = (term: InstallmentTerm) => ({
  id: term.id ?? String(term.duration),
  name: `${term.duration} ${t.XXVX(monthMsg)}`,
  description: formatInstallmentPrice(term.priceWithoutTax, false),
});

const formatEppTermSummaryText = (term: InstallmentTerm | undefined): string => {
  if (!term) {
    return '';
  }
  const duration = term.duration ?? 1;
  const totalPrice = formatSum(eurosToCents((term.priceWithoutTax ?? 0) * duration), true) ?? '';
  return [
    `${duration} ${t.XXVX(monthMsg)} ${t.C8I6(contractMsg)}`,
    `${t.W1RX(totalWithCurlyBracketsMsg, totalPrice)} €, ${t.S8TX(alvZeroMsg)}`,
  ].join(' ');
};

export const DeviceDetailsPaymentCoCo = ({
  eppInstallmentTerms,
  deviceInstallmentOptions,
  selectedDeviceInstallmentId,
  onSelectDeviceInstallment,
  selectedEppInstallmentTermId = '',
  onSelectEppInstallmentTerm,
}: DeviceDetailsPaymentCoCoProps) => {
  const eppProducts = (eppInstallmentTerms ?? []).map(installmentTermToSelectItem);
  const deviceProducts = deviceInstallmentOptions.map(installmentOptionToSelectItem);
  const eppTermSummaryText = formatEppTermSummaryText(
    eppInstallmentTerms?.find(term => term.id === selectedEppInstallmentTermId)
  );

  if (!eppProducts.length && !deviceProducts.length) {
    return null;
  }

  return (
    <div className="of-device-details-payment">
      {eppProducts.length > 0 && deviceProducts.length > 0 && <h4>{t.D002(purchaseOptionsMsg)}</h4>}
      {eppProducts.length > 0 && (
        <div className="of-device-details-payment__section">
          <div className="of-device-details-payment__section-header">
            <label className="of-device-details-payment__section-label">{t.JJM2(elisaDevicesServiceMsg)}</label>
            <CL.Icon icon="information" size="s" color="primary" />
          </div>
          <CL.SelectGroup
            className={dsClass.PADDING_0}
            products={eppProducts}
            onChange={id => id && onSelectEppInstallmentTerm?.(id as string)}
            value={selectedEppInstallmentTermId}
          />
          <span className={dsClass.TEXT_XS}>{eppTermSummaryText}</span>
        </div>
      )}
      {deviceProducts.length > 0 && (
        <div className="of-device-details-payment__section">
          <div className="of-device-details-payment__section-header">
            <label className="of-device-details-payment__section-label">{t.QL8F(purchasedDeviceMsg)}</label>
            <CL.Icon icon="information" size="s" color="primary" />
          </div>
          <CL.SelectGroup
            className={dsClass.PADDING_0}
            products={deviceProducts}
            onChange={id => id && onSelectDeviceInstallment(id as string)}
            value={selectedDeviceInstallmentId}
          />
        </div>
      )}
    </div>
  );
};
